'use client';

import * as React from 'react';
import { Select as SelectPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';
import { ChevronDownIcon, CheckIcon, ChevronUpIcon } from 'lucide-react';

function Select({ ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
    return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
    return (
        <SelectPrimitive.Group
            data-slot="select-group"
            className={cn('scroll-my-1', className)}
            {...props}
        />
    );
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
    return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
    className,
    size = 'default',
    children,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
    size?: 'sm' | 'default' | 'lg';
}) {
    return (
        <SelectPrimitive.Trigger
            data-slot="select-trigger"
            data-size={size}
            className={cn(
                // `overflow-hidden` + `min-w-0` on the value slot keep a long option
                // label (e.g. a wide CSV header) clipped inside the control instead of
                // painting over whatever sits to its right. The focus ring is a
                // box-shadow, so it is not clipped by this.
                "flex w-fit items-center justify-between gap-1.5 overflow-hidden rounded-md border border-neutral-200 bg-bg-surface py-2 pe-2 ps-3 text-sm text-text-primary whitespace-nowrap transition-[border-color,box-shadow] duration-150 outline-none select-none cursor-pointer hover:border-neutral-300 focus-visible:border-primary-300 focus-visible:shadow-[0_0_0_2px_var(--color-primary-100)] disabled:cursor-not-allowed disabled:opacity-50 data-placeholder:text-neutral-400 data-[size=default]:h-9 data-[size=sm]:h-8 data-[size=sm]:text-[0.8125rem] data-[size=lg]:h-10 data-[size=lg]:text-base *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:min-w-0 *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 dark:bg-bg-surface dark:border-border-default dark:hover:border-border-strong dark:focus-visible:border-primary-600 dark:focus-visible:shadow-[0_0_0_2px_var(--color-primary-900)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                className,
            )}
            {...props}
        >
            {children}
            <SelectPrimitive.Icon asChild>
                <ChevronDownIcon className="pointer-events-none size-4 text-text-tertiary" />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    );
}

function SelectContent({
    className,
    children,
    // `popper` anchors the menu to the trigger. The Radix default,
    // `item-aligned`, anchors the *selected* option over the trigger instead —
    // native-`<select>` behaviour — so picking a later option threw the whole
    // list upward across the page on reopen, and it always painted over the
    // control you had just clicked.
    position = 'popper',
    align = 'start',
    sideOffset = 4,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Content
                data-slot="select-content"
                data-align-trigger={position === 'item-aligned'}
                className={cn(
                    // `min-w` tracks the trigger so the menu lines up with the
                    // control and still grows past it for a long label. A fixed
                    // width instead left narrow triggers (a 70px "rows per page")
                    // sprouting a popup that hung off their edge.
                    'relative z-50 max-h-(--radix-select-content-available-height) min-w-(--radix-select-trigger-width) origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md bg-bg-surface text-text-primary shadow-md border border-border-default duration-100 data-[align-trigger=true]:animate-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                    className,
                )}
                position={position}
                align={align}
                sideOffset={sideOffset}
                {...props}
            >
                <SelectScrollUpButton />
                <SelectPrimitive.Viewport
                    data-position={position}
                    className={cn(
                        // `p-1` lives here rather than on the content so the
                        // scroll up/down buttons still sit flush at the edges,
                        // and so the rare `SelectGroup` does not double it up.
                        //
                        // `min-h`, not `h` — a fixed height equal to the trigger
                        // would clamp the popper-positioned list to one row and
                        // hide every option below it.
                        'p-1 data-[position=popper]:min-h-(--radix-select-trigger-height) data-[position=popper]:w-full data-[position=popper]:min-w-(--radix-select-trigger-width)',
                    )}
                >
                    {children}
                </SelectPrimitive.Viewport>
                <SelectScrollDownButton />
            </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
    );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
    return (
        <SelectPrimitive.Label
            data-slot="select-label"
            className={cn('px-1.5 py-1 text-xs text-text-tertiary font-medium', className)}
            {...props}
        />
    );
}

function SelectItem({
    className,
    children,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
    return (
        <SelectPrimitive.Item
            data-slot="select-item"
            className={cn(
                "relative flex w-full cursor-default items-center gap-1.5 rounded-sm py-1.5 pe-8 ps-2 text-sm outline-hidden select-none transition-colors duration-150 focus:bg-bg-hover focus:text-text-primary data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
                className,
            )}
            {...props}
        >
            <span className="pointer-events-none absolute end-2 flex size-4 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                    <CheckIcon className="pointer-events-none size-4 text-primary-500" />
                </SelectPrimitive.ItemIndicator>
            </span>
            <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </SelectPrimitive.Item>
    );
}

function SelectSeparator({
    className,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
    return (
        <SelectPrimitive.Separator
            data-slot="select-separator"
            className={cn('pointer-events-none -mx-1 my-1 h-px bg-border-default', className)}
            {...props}
        />
    );
}

function SelectScrollUpButton({
    className,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
    return (
        <SelectPrimitive.ScrollUpButton
            data-slot="select-scroll-up-button"
            className={cn(
                "z-10 flex cursor-default items-center justify-center bg-bg-surface py-1 [&_svg:not([class*='size-'])]:size-4",
                className,
            )}
            {...props}
        >
            <ChevronUpIcon />
        </SelectPrimitive.ScrollUpButton>
    );
}

function SelectScrollDownButton({
    className,
    ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
    return (
        <SelectPrimitive.ScrollDownButton
            data-slot="select-scroll-down-button"
            className={cn(
                "z-10 flex cursor-default items-center justify-center bg-bg-surface py-1 [&_svg:not([class*='size-'])]:size-4",
                className,
            )}
            {...props}
        >
            <ChevronDownIcon />
        </SelectPrimitive.ScrollDownButton>
    );
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
};
